import AppConfig from 'config/AppConfig';
import axios, { AxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';

interface RequestParameters {
  [key: string]: string | string[] | number | boolean;
}

function createDevHeaders(): Record<string, string> {
  if (!AppConfig.devMode) {
    return {};
  }

  const devHeaders: Record<string, string> = {};

  if (AppConfig.userIdClaim !== undefined) {
    devHeaders['x-claim-userId'] = AppConfig.userIdClaim;
  }

  if (AppConfig.userTypeClaim !== undefined) {
    devHeaders['x-claim-userType'] = AppConfig.userTypeClaim;
  }

  if (AppConfig.agencyClaim !== undefined) {
    devHeaders['x-claim-agency'] = AppConfig.agencyClaim;
  }

  if (AppConfig.rolesClaim !== undefined) {
    devHeaders['x-claim-roles'] = AppConfig.rolesClaim;
  }

  return devHeaders;
}

export class AgxApi {
  private baseUrl: string;
  private authToken: string;
  private headers: {};

  constructor(
    service: string,
    authToken: string,
    uri: string | null = null,
    headers: { [key: string]: string } = {}
  ) {
    const serviceUri = resolveServiceUri(service, uri);

    this.baseUrl = `${serviceUri.replace(/\/$/, '')}/`;

    this.authToken = authToken;

    Object.assign(headers, createDevHeaders());
    headers['x-session-id'] =
      sessionStorage.getItem(http.sessionName) ?? uuidv4();
    this.headers = headers;
  }

  get<T>(
    url: string,
    queryParameters: any = {},
    signal?: AbortSignal,
    onUploadProgress?: (progressEvent: any) => void
  ) {
    return axios.get<T>(
      `${this.baseUrl}${url}`,
      this.createParameters(queryParameters, signal, onUploadProgress)
    );
  }

  post<T>(
    url: string,
    body?: any,
    queryParameters: any = {},
    signal?: AbortSignal,
    onUploadProgress?: (progressEvent: any) => void
  ) {
    return axios.post<T>(
      `${this.baseUrl}${url}`,
      body,
      this.createParameters(queryParameters, signal, onUploadProgress)
    );
  }

  put<T>(
    url: string,
    body?: any,
    signal?: AbortSignal,
    onUploadProgress?: (progressEvent: any) => void
  ) {
    return axios.put<T>(
      `${this.baseUrl}${url}`,
      body,
      this.createParameters(undefined, signal, onUploadProgress)
    );
  }

  patch<T>(
    url: string,
    body?: any,
    signal?: AbortSignal,
    onUploadProgress?: (progressEvent: any) => void
  ) {
    return axios.patch<T>(
      `${this.baseUrl}${url}`,
      body,
      this.createParameters(undefined, signal, onUploadProgress)
    );
  }

  delete<T>(
    url: string,
    signal?: AbortSignal,
    onUploadProgress?: (progressEvent: any) => void
  ) {
    return axios.delete<T>(
      `${this.baseUrl}${url}`,
      this.createParameters(undefined, signal, onUploadProgress)
    );
  }

  createParameters = (
    queryParameters: any = {},
    signal?: AbortSignal,
    onUploadProgress?: (progressEvent: any) => void
  ) => {
    let config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + this.authToken,
        ...this.headers,
      },
      params: {
        ...queryParameters,
      },
      paramsSerializer: (params: RequestParameters): string => {
        return Object.keys(params)
          .filter(key => {
            const value = params[key];
            return value !== null && value !== undefined && value !== '';
          })
          .map(key => {
            const value = params[key];
            return Array.isArray(value)
              ? value.map(item => encodedUrlParam(key, item)).join('&')
              : encodedUrlParam(key, value);
          })
          .join('&');
      },
      signal: signal,
      onUploadProgress: onUploadProgress,
    };

    return config;
  };
}

function resolveServiceUri(service: string, uri: string | null): string {
  if (uri) {
    return uri;
  }

  // ./dev up can start only some backend services locally. In dev mode this
  // lets those services point at localhost while the rest keep using the normal
  // environment base API URL.
  const normalizedService = service.toLowerCase();
  const localUri = AppConfig.devMode
    ? AppConfig.localUris?.[normalizedService]
    : undefined;

  if (localUri) {
    return localUri;
  }

  const baseUri = import.meta.env.VITE_REACT_APP_AGENTX_BASE_API;
  return `${baseUri}/${service}`;
}

const encodedUrlParam = (
  key: string,
  value: string | number | boolean | string[]
): string => {
  if (Array.isArray(value)) {
    return value.map((item: string) => encodedUrlParam(key, item)).join('&');
  }

  const encodedKey = encodeURIComponent(key);
  let encodedValue = 'null';

  if (value !== null && value !== undefined)
    encodedValue = encodeURIComponent(value);

  return `${encodedKey}=${encodedValue}`;
};

export const http = {
  get<T>(url: string) {
    return axios.get<T>(url);
  },
  post<T>(url: string, body?: any) {
    return axios.post<T>(url, body);
  },
  put<T>(url: string, body?: any) {
    return axios.put<T>(url, body);
  },
  patch<T>(url: string, body?: any) {
    return axios.patch<T>(url, body);
  },
  delete<T>(url: string) {
    return axios.delete<T>(url);
  },
  sessionName: 'autopilot-sessionId',
};
